import { BettingStructure, GameType, GameVariantId, HandMetadata } from './types';

export const formatAmount = (value: number, gameType: GameType) => {
  const sign = value > 0 ? '+' : value < 0 ? '-' : '';
  const absolute = Math.abs(value);

  if (gameType === 'cash') {
    return `${sign}$${absolute.toFixed(2)}`;
  }

  return `${sign}${Math.round(absolute).toLocaleString('en-US')}`;
};

export const formatPot = (value: number, gameType: GameType) => {
  if (gameType === 'cash') return `$${value.toFixed(2)}`;
  return Math.round(value).toLocaleString('en-US');
};

export const formatGameVariant = (
  gameVariant: GameVariantId,
  bettingStructure: BettingStructure
) => {
  if (gameVariant === 'texas') return "Hold'em";
  return bettingStructure.includes('HiLo') ? 'PLO8' : 'PLO';
};

export const formatMetadata = (metadata: HandMetadata, gameType: GameType) => {
  if (gameType === 'cash') {
    return `$${(metadata.smallBlind ?? 0).toFixed(2)} / $${(metadata.bigBlind ?? 0).toFixed(2)}`;
  }

  const blinds = `${metadata.smallBlind ?? 0} / ${metadata.bigBlind ?? 0}`;
  const ante = metadata.ante ? ` ante ${metadata.ante}` : '';
  return `Level ${metadata.level ?? '-'} - ${blinds}${ante}`;
};

export const formatHandTime = (ts?: number) => {
  if (!ts) return '';
  return formatTimestamp(ts);
};

export const formatEventTime = (ts?: number) => {
  if (!ts) return '';

  const value = Number(ts);
  if (!Number.isFinite(value) || value <= 0) return '';

  return formatTimestamp(value < 1e12 ? value * 1000 : value);
};

/**
 * The session date, shown once in the panel header so a support screenshot
 * carries the day without repeating it on every row: "Wed, Sep 23".
 * Accepts seconds or milliseconds like formatEventTime.
 */
export const formatSessionDate = (ts?: number) => {
  const value = Number(ts);
  const ms =
    Number.isFinite(value) && value > 0 ? (value < 1e12 ? value * 1000 : value) : Date.now();
  return new Date(ms).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric'
  });
};

/** Date metadata used by the alternative playground layouts. */
export const formatHandDate = (ts: number) => {
  const date = new Date(ts);
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

export const formatPlayerDisplayName = (name: string, isViewer?: boolean) =>
  isViewer ? `${name} (You)` : name;

/**
 * The panel's one clock: 12-hour, to the minute - "5:21 PM". Seconds were
 * dropped on 2026-09-23 (Jason): rows are already in order, and the extra
 * digits only added noise next to the amounts.
 */
const formatTimestamp = (ts: number) =>
  new Date(ts).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });

/** A hand card's header time - the same clock as its rows. Accepts seconds or milliseconds. */
export const formatClockTime = (ts?: number) => formatEventTime(ts);
